import { ArrowRight, MapPin } from "lucide-react";

const HERO_IMG = "/hero/jayaram-ngo-work.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Left: Headline */}
          <div className="lg:col-span-7 animate-fade-up">
            <p className="overline mb-6" data-testid="hero-overline">
              <span className="inline-block w-8 h-px bg-forest align-middle mr-3" />
              Est. 2007 — Building neighborhoods, not buildings
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[0.95] tracking-tight text-balance text-ink"
              data-testid="hero-headline"
            >
              The city <em className="italic text-terracotta font-medium">belongs</em> to the
              <br className="hidden md:block" />
              people who <span className="text-forest">build it</span>.
            </h1>
            <p
              className="mt-8 max-w-xl text-base md:text-lg text-graphite leading-relaxed"
              data-testid="hero-subhead"
            >
              Jharkhand Rastriya Sarva Dharma Welfare Association works
              alongside residents of India's fastest-growing cities — turning
              informal settlements into dignified, resilient neighborhoods
              through housing, livelihoods, education and civic power.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#donate"
                data-testid="hero-cta-donate"
                className="group inline-flex items-center gap-3 bg-terracotta hover:bg-terracotta-hover text-white px-8 py-4 transition-colors"
              >
                Donate now
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#programs"
                data-testid="hero-cta-programs"
                className="inline-flex items-center gap-2 text-ink hover:text-terracotta px-2 py-4 transition-colors border-b border-ink hover:border-terracotta"
              >
                See our work
              </a>
            </div>

            <div className="mt-12 flex items-center gap-6">
              <div className="flex -space-x-3">
                {[
                  "/imgs/indian-portrait-1.jpg",
                  "/imgs/indian-portrait-2.jpg",
                  "/imgs/indian-portrait-3.jpg",
                ].map((u, i) => (
                  <img
                    key={i}
                    src={u}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border-2 border-sand"
                  />
                ))}
              </div>
              <p className="text-sm text-graphite max-w-xs">
                Joined by <span className="text-ink font-medium">1,900+</span> volunteers
                across 62 neighborhoods this year.
              </p>
            </div>
          </div>

          {/* Right: Images — main hero + award photo stacked */}
          <div className="lg:col-span-5 relative pb-20 md:pb-6">
            <div className="relative aspect-[4/5] grain overflow-hidden bg-line">
              <img
                src={HERO_IMG}
                alt="Dr. Jayaram Das distributing blankets at International Human Rights Day"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
              <div className="absolute left-4 bottom-4 right-4 flex items-center justify-between">
                <div className="bg-cream/95 backdrop-blur-sm px-4 py-3 max-w-[78%]">
                  <p className="overline mb-1 text-[10px]">In focus</p>
                  <p className="text-sm text-ink font-medium leading-snug">
                    Human Rights Day, Dec '24 — Blanket distribution, Jamshedpur.
                  </p>
                </div>
                <div className="bg-forest text-cream w-12 h-12 flex items-center justify-center">
                  <MapPin size={18} strokeWidth={1.5} />
                </div>
              </div>
            </div>

            {/* Award photo — overlapping card */}
            <div
              className="absolute -bottom-16 right-0 md:-right-8 w-40 md:w-52 rotate-[2.5deg] border-[6px] border-cream shadow-[0_18px_50px_-20px_rgba(26,28,26,0.45)] bg-cream"
              data-testid="hero-award-photo"
            >
              <div className="aspect-[4/3] overflow-hidden bg-line">
                <img
                  src="/hero/gj-das-award.jpg"
                  alt="Dr. Jayaram Das receiving the honour from Indian Journalist Association"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="px-3 py-2 text-[11px] leading-snug text-graphite font-medium">
                Honoured by Indian Journalist Association, 2026
              </p>
            </div>

            <div className="hidden md:block absolute -bottom-6 -left-6 bg-mustard text-ink px-5 py-3 font-serif text-2xl leading-none rotate-[-3deg]">
              +12,400 children in school
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
